import { RowSelectionState, ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { FbLink } from 'components/FbUI/FbLink'
import { cn } from 'components/UI/cn'
import { Dispatch, SetStateAction, useRef, useMemo } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'
import { useContainerHeight } from './useContainerHeight'

function getEffectiveScore(row: CSVRow): number {
  const door = row[InternalColumns.MATCHED_DOOR]
  const chain = row[InternalColumns.MATCHED_CHAIN]
  return door?.score ?? chain?.score ?? 0
}

/** Compute percentile thresholds from the dataset so coloring adapts to actual score ranges. */
function computeScoreThresholds(data: CSVRow[]): { p25: number; p10: number } {
  const scores = data.map(getEffectiveScore).sort((a, b) => a - b)
  if (scores.length === 0) return { p25: 0, p10: 0 }
  return {
    p25: scores[Math.floor(scores.length * 0.25)] ?? 0,
    p10: scores[Math.floor(scores.length * 0.1)] ?? 0,
  }
}

function getScoreTierClasses(
  score: number,
  thresholds: { p25: number; p10: number }
): string {
  if (score > thresholds.p25) return 'bg-success'
  if (score > thresholds.p10) return 'bg-warning'
  return 'bg-error'
}

const INITIAL_ORDERING = [{ id: 'match_score', desc: false }]

function fieldsMatch(input: string, matched: string): boolean {
  if (!input || !matched || input === '-' || matched === '-') return true
  const a = input.toLowerCase().trim()
  const b = matched.toLowerCase().trim()
  return b.includes(a) || a.includes(b)
}

export function ImportWizardConfirmMatchedStep({
  show,
  headers,
  setRowSelection,
  rowSelection,
  data,
  loading,
}: {
  show: boolean
  headers: Record<string, string>
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  rowSelection: RowSelectionState
  data: CSVRow[]
  loading: boolean
}) {
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const [containerRef, containerHeight] = useContainerHeight()

  const scoreThresholds = useMemo(() => computeScoreThresholds(data), [data])
  const scoreThresholdsRef = useRef(scoreThresholds)
  scoreThresholdsRef.current = scoreThresholds

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    return [
      {
        id: 'match_score',
        header: 'Score',
        size: 80,
        accessorFn: (row) => getEffectiveScore(row),
        cell: ({ row }) => {
          const score = getEffectiveScore(row.original)

          return (
            <div className="flex items-center gap-1.5">
              <div
                className={cn(
                  'w-2.5 h-2.5 rounded-full flex-shrink-0',
                  getScoreTierClasses(score, scoreThresholdsRef.current)
                )}
              />
              <span className="text-xxs text-fg-muted">
                {Math.round(score)}
              </span>
            </div>
          )
        },
      },
      {
        id: 'input_data',
        header: 'Your Data',
        accessorFn: (row) => (row[headers.name] || '').toLowerCase(),
        cell: ({ row }) => {
          const assembledAddress = [
            row.original[headers.street],
            row.original[headers.city],
            row.original[headers.state],
            row.original[headers.zip],
          ]
            .filter(Boolean)
            .join(', ')

          const name = row.original[headers.name] || '-'
          const domain = row.original[headers.domain] || '-'
          const address =
            row.original[headers.full_address] || assembledAddress || '-'

          return (
            <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Name
              </span>
              <span className="text-fg truncate">{name}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Domain
              </span>
              <span className="text-fg truncate">{domain}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Address
              </span>
              <span className="text-fg truncate">{address}</span>
            </div>
          )
        },
      },
      {
        id: 'matched_chain',
        header: 'Matched Chain',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          return (chain?.name || '').toLowerCase()
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          if (!chain) return <span className="text-sm text-fg-muted">-</span>

          const inputName = row.original[headers.name] || ''
          const inputDomain = row.original[headers.domain] || ''
          const nameMatch = fieldsMatch(inputName, chain.name || '')
          const domainMatch = fieldsMatch(inputDomain, chain.domain || '')
          const chainUrl = getCompanyUrlFromId(`${chain.type}${chain.item_id}`)

          return (
            <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Name
              </span>
              <span
                className={cn(
                  'truncate',
                  !nameMatch ? 'bg-warning-bg text-fg rounded px-1' : 'text-fg'
                )}
              >
                {chainUrl ? (
                  <FbLink to={chainUrl} target="_blank">
                    {chain.name}
                  </FbLink>
                ) : (
                  chain.name
                )}
              </span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Domain
              </span>
              <span
                className={cn(
                  'truncate',
                  !domainMatch
                    ? 'bg-warning-bg text-fg rounded px-1'
                    : 'text-fg'
                )}
              >
                {chain.domain || '-'}
              </span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Cities
              </span>
              <span
                className="text-fg truncate"
                title={chain.city_summary || '-'}
              >
                {chain.city_summary || '-'}
              </span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                States
              </span>
              <span
                className="text-fg truncate"
                title={chain.state_summary || '-'}
              >
                {chain.state_summary || '-'}
              </span>
            </div>
          )
        },
      },
      {
        id: 'matched_type',
        header: 'Matched Type',
        size: 120,
        accessorFn: (row) => {
          const door = row[InternalColumns.MATCHED_DOOR]
          const chain = row[InternalColumns.MATCHED_CHAIN]
          return getOppTypeLabel(door?.type || chain?.type) || ''
        },
        cell: ({ row }) => {
          const door = row.original[InternalColumns.MATCHED_DOOR]
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          const typeCode = door?.type || chain?.type
          if (!typeCode) return '-'
          return <OppTypeBadge typeCode={typeCode} />
        },
      },
    ]
    // scoreThresholds accessed via ref to avoid recreating columns on data changes
  }, [headers, getCompanyUrlFromId])

  if (!show) {
    return null
  }

  return (
    <div ref={containerRef} className="h-full overflow-hidden">
      <ClientDataTable
        loading={loading}
        selectedRows={rowSelection}
        onRowSelectionChange={setRowSelection}
        data={data}
        height={containerHeight || 400}
        columns={columnDefs}
        enableSorting
        enableRowSelection
        invertedRowSelection
        selectionRowID={InternalColumns.COMPANY_HASH}
        initialOrdering={INITIAL_ORDERING}
      />
    </div>
  )
}
